'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent } from '@/lib/tools/types';
import { Calculator, Copy, Check, RotateCcw, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { logToolUsage } from '@/lib/tools/analytics';
import { ToolSignupWall } from './tool-signup-wall';

interface VatCalculatorProps {
  locale: Locale;
  content: ToolContent;
}

type CalculationType = 'grossToNet' | 'netToGross';

const VAT_RATES = [21, 9, 0];

export function VatCalculator({ locale, content }: VatCalculatorProps) {
  const [amount, setAmount] = useState<string>('');
  const [vatRate, setVatRate] = useState<number>(21);
  const [calculationType, setCalculationType] = useState<CalculationType>('grossToNet');
  const [copied, setCopied] = useState<string | null>(null);
  const hasLoggedRef = useRef(false);

  const labels = content.labels;

  // Log tool usage once when user performs first calculation
  useEffect(() => {
    const numAmount = parseFloat(amount.replace(',', '.'));
    if (!hasLoggedRef.current && !isNaN(numAmount) && numAmount > 0) {
      hasLoggedRef.current = true;
      logToolUsage({
        tool_name: 'vat-calculator',
        locale,
      });
    }
  }, [amount, locale]);

  const calculateVat = useCallback(() => {
    const numAmount = parseFloat(amount.replace(',', '.'));
    if (isNaN(numAmount) || numAmount <= 0) {
      return null;
    }

    if (calculationType === 'grossToNet') {
      const netAmount = numAmount / (1 + vatRate / 100);
      const vatAmount = numAmount - netAmount;
      return {
        netAmount,
        vatAmount,
        grossAmount: numAmount,
      };
    } else {
      const vatAmount = numAmount * (vatRate / 100);
      const grossAmount = numAmount + vatAmount;
      return {
        netAmount: numAmount,
        vatAmount,
        grossAmount,
      };
    }
  }, [amount, vatRate, calculationType]);

  const result = calculateVat();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const reset = () => {
    setAmount('');
    setVatRate(21);
    setCalculationType('grossToNet');
  };

  return (
    <div className="mx-auto max-w-2xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100">
          <Calculator className="h-7 w-7 text-green-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {content.title}
        </h2>
        <p className="mt-3 text-lg text-slate-600">
          {content.description}
        </p>
      </div>

      {/* Calculator Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Calculation Type Toggle */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            {labels.calculationType}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setCalculationType('grossToNet')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                calculationType === 'grossToNet'
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels.grossToNet}
            </button>
            <button
              type="button"
              onClick={() => setCalculationType('netToGross')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                calculationType === 'netToGross'
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels.netToGross}
            </button>
          </div>
        </div>

        {/* Amount Input */}
        <div className="mb-6">
          <label htmlFor="amount" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.amount}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">
              €
            </span>
            <input
              type="text"
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="block w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        {/* VAT Rate Selection */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            {labels.vatRate}
          </label>
          <div className="flex gap-2">
            {VAT_RATES.map((rate) => (
              <button
                key={rate}
                type="button"
                onClick={() => setVatRate(rate)}
                className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                  vatRate === rate
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {rate}%
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="mt-6 space-y-3 rounded-xl bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{labels.netAmount}</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">
                  {formatCurrency(result.netAmount)}
                </span>
                <button
                  onClick={() => copyToClipboard(result.netAmount.toFixed(2), 'net')}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                  title={labels.copyResult}
                >
                  {copied === 'net' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{labels.vatAmount} ({vatRate}%)</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">
                  {formatCurrency(result.vatAmount)}
                </span>
                <button
                  onClick={() => copyToClipboard(result.vatAmount.toFixed(2), 'vat')}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                  title={labels.copyResult}
                >
                  {copied === 'vat' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">{labels.grossAmount}</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-slate-900">
                    {formatCurrency(result.grossAmount)}
                  </span>
                  <button
                    onClick={() => copyToClipboard(result.grossAmount.toFixed(2), 'gross')}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                    title={labels.copyResult}
                  >
                    {copied === 'gross' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.reset}
        </button>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      {/* Signup Wall */}
      <ToolSignupWall locale={locale} />

      {/* CTA Banner */}
      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Instructions */}
      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment ça marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">
                  {index + 1}
                </span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Disclaimer */}
      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            {content.disclaimer.heading}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            {content.disclaimer.content}
          </p>
        </div>
      )}
    </div>
  );
}
